import { useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../../contexts/DialogContext';
import { window } from '../../../../globals';
import { selectOptionFactory } from '../../../../utils';
import Section from '../../../shared/settings/SettingsPageSection';
import Dropdown from '../../shared/SettingsPageDropdown';
import useOrganizationSettings from '../../shared/useOrganizationSettings';
import type { ProjectPrivacySettings } from '../../../../../shared/settings/project/privacy';
import type { SelectOption } from '../../../../types';
import type { OrganizationSettingsEndpoint } from '../../shared/useOrganizationSettings';

import PropagatePrivacySettingsModal from './PropagatePrivacySettingsModal';

type PrivateOption = SelectOption<ProjectPrivacySettings['defaultProjectPrivacy']>;

const unsetPrivacyOption: PrivateOption = selectOptionFactory(null, 'None');
const publicPrivacyOption: PrivateOption = selectOptionFactory('public', 'Public');
const privatePrivacyOption: PrivateOption = selectOptionFactory('private', 'Private');

//  Returns the SelectOption for whatever the selected privacy option is.
const getSelectedPrivacyOption = (defaultProjectPrivacy?: ProjectPrivacySettings['defaultProjectPrivacy']) => {
  if (defaultProjectPrivacy === 'public') {
    return publicPrivacyOption;
  }
  if (defaultProjectPrivacy === 'private') {
    return privatePrivacyOption;
  }
  return unsetPrivacyOption;
};

const DEFAULT_PRIVACY_OPTIONS: PrivateOption[] = [unsetPrivacyOption, publicPrivacyOption, privatePrivacyOption];

/**
 * Displays the privacy section of the project general settings page.This section
 * allows users to change privacy level for new projects abd propagate the changes to existing projects.
 */
const PrivacySection: VoidFunctionComponent = () => {
  const { onConfirm } = useDialogContext();
  const [isPropagatePrivacyModalOpen, setIsPropagatePrivacyModalOpen] = useState<boolean>(false);
  const endpoint = useMemo<OrganizationSettingsEndpoint>(
    () => `/api/organizations/${window.user?.organizationId ?? 0}/settings/projects/privacy`,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [window.user?.organizationId],
  );
  const { isDirty, onUpdateSettings, settings, isLoading, onSaveSettings, isSaveButtonDisabled } =
    useOrganizationSettings<ProjectPrivacySettings>({
      endpoint,
      settingsSection: 'privacy',
    });

  const onSave = () => {
    // Display a confirmation dialog if the user is changing the default project privacy to private or public.
    if (settings?.defaultProjectPrivacy !== null) {
      return onConfirm({
        title: 'Confirm Access Toggle',
        body: (
          <div>
            Are you sure you want to change how people can access your projects? <br />
            <br />
            You will still need to save your changes to complete this change.
          </div>
        ),
        onConfirm: onSaveSettings,
      });
    }
    return onSaveSettings();
  };

  return (
    <>
      <Section>
        <Section.Left heading="Privacy">
          <Section.Paragraph>Sets the default privacy level for new projects.</Section.Paragraph>
          <Section.Buttons>
            <Section.PropagateButton isDisabled={isDirty} onClick={() => setIsPropagatePrivacyModalOpen(true)} />
            <Section.SaveButton onClick={onSave} isLoading={isLoading.save} isDisabled={isSaveButtonDisabled} />
          </Section.Buttons>
        </Section.Left>
        <Section.Right>
          <Dropdown
            id="settings-default-project-privacy"
            label="Default Project Privacy"
            options={DEFAULT_PRIVACY_OPTIONS}
            onChange={(option) =>
              option &&
              onUpdateSettings({
                defaultProjectPrivacy: (option?.value ?? null) as ProjectPrivacySettings['defaultProjectPrivacy'],
              })}
            value={getSelectedPrivacyOption(settings?.defaultProjectPrivacy)}
            isDisabled={isLoading.any}
          />
        </Section.Right>
      </Section>
      {settings && (
        <PropagatePrivacySettingsModal
          isOpen={isPropagatePrivacyModalOpen}
          settings={settings}
          onHide={() => setIsPropagatePrivacyModalOpen(false)}
          endpoint={endpoint}
        />
      )}
    </>
  );
};

export default PrivacySection;
